import { makeEntityScaffold } from '@classpass/saga-tools';
import { transformClientResponse } from 'helpers/core';

import URLS from 'constants/urls';

const get = (httpClient, venueId, includeClassLevel) => {
  const endpoint = URLS.venue.opportunities(venueId, includeClassLevel);

  return httpClient.get(endpoint).then(response => {
    const { opportunities } = transformClientResponse(response);
    const classLevelOpportunities = [];
    const scheduleLevelOpportunities = [];
    opportunities.forEach(opportunity => {
      // eslint-disable-next-line no-unused-expressions
      opportunity.classDisabled
        ? classLevelOpportunities.push(opportunity)
        : scheduleLevelOpportunities.push(opportunity);
    });
    // return opps as class level first, then schedule level while perserving
    // existing sorting within those categories in O(n) time
    return [...classLevelOpportunities, ...scheduleLevelOpportunities];
  });
};

const act = (httpClient, venueId, scheduleId, opportunities, spotsAdded, isRecurring, viaEmail, includeClassLevel) => {
  const endpoint = URLS.venue.opportunitiesAct(venueId);

  const data = {
    opportunities: [
      {
        schedule_id: scheduleId,
        spots_added: spotsAdded,
        is_recurring: isRecurring,
      },
    ],
    via_email: viaEmail,
  };

  return httpClient.post(endpoint, data).then(() => {
    // when accepting an opp from the opps page
    if (includeClassLevel) {
      return opportunities.filter(opportunity => opportunity.scheduleId !== scheduleId);
    }
    // when accepting a teaser opp (from the homepage) or the classes page
    // on the homepage, `act` returns all the opps because we want to show, on the opp, that the call has succeeded before removing it from state.
    // This is handled by the `doActOnTeaserOpportunity` saga pausing and then getting the updated list via another call
    // on the classes page it doesn't seem to make a difference
    return opportunities;
  });
};

const actOnAll = (httpClient, venueId, opportunities, actedOnOpportunities, viaEmail) => {
  const endpoint = URLS.venue.opportunitiesAct(venueId);
  // eslint-disable-next-line camelcase
  const opportunitiesForAPI = actedOnOpportunities.map(([schedule_id, spots_added, is_recurring]) => ({
    schedule_id,
    spots_added,
    is_recurring,
  }));
  const data = {
    opportunities: opportunitiesForAPI,
    via_email: viaEmail,
  };
  // accepted all individual schedule instance spots, so only class level remain
  return httpClient.post(endpoint, data).then(() => opportunities.filter(({ classDisabled }) => classDisabled));
};

const handler = (
  { httpClient },
  {
    venueId,
    scheduleId,
    actedOnOpportunities,
    opportunities,
    spotsAdded,
    isRecurring = false,
    viaEmail = false,
    includeClassLevel = false,
  }
) => {
  if (scheduleId && spotsAdded && !actedOnOpportunities) {
    return act(httpClient, venueId, scheduleId, opportunities, spotsAdded, isRecurring, viaEmail, includeClassLevel);
  }
  if (actedOnOpportunities && !scheduleId && !spotsAdded) {
    return actOnAll(httpClient, venueId, opportunities, actedOnOpportunities, viaEmail);
  }

  return get(httpClient, venueId, includeClassLevel);
};

export default makeEntityScaffold('SAGA.API.VENUE.OPPORTUNITIES', handler, {
  defaultKey: (_, { venueId }) => venueId,
});
